import { Component, inject, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CompanyView } from '@api-proxies';
import { EntityCardSection } from '@enums/EntityCardSection';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { customFieldPlacement, ICardConfig } from '@utils/custom-field-placement';
import { GenericCardComponent, ObjectExtensionsService, ValidatorType } from 'ngx-lib';

@Component({
    selector: 'app-companies-single-overview',
    templateUrl: './companies-single-overview.component.html',
    styleUrls: ['./companies-single-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericCardComponent]
})
export class CompaniesSingleOverviewComponent implements OnChanges {
    @ViewChild('mainInfoCard')
    mainInfoCard?: GenericCardComponent;

    @ViewChild('customInfoCard')
    customInfoCard?: GenericCardComponent;

    @Input()
    company?: CompanyView;

    @Input()
    isReadOnly?: boolean;

    factory: AppEditorFactory;

    entityCardSection: typeof EntityCardSection;
    cardsConfig: ICardConfig;

    accountHasArchived = false;
    buildingHasArchived = false;
    usersHasArchived = false;
    contactHasArchived = false;

    private readonly entityCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    constructor() {
        this.factory = new AppEditorFactory();
        this.cardsConfig = [];
        this.entityCardSection = EntityCardSection;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (
            this.company &&
            changes['company'] &&
            !this.objectExtensionService.isEqualTo(changes['company'].currentValue, changes['company'].previousValue)
        ) {
            this.cardsConfig = {
                [EntityCardSection.CompanyMainInfo]: [
                    {
                        label: 'Name',
                        componentId: EditorType.textInput,
                        fieldName: 'name',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        validations: [
                            { type: ValidatorType.required },
                            { type: ValidatorType.string, args: { maxLength: 100 } }
                        ],
                        additionalInfo: {
                            shouldTrimSpaces: true
                        }
                    },
                    {
                        label: 'Abbreviation',
                        componentId: EditorType.textInput,
                        fieldName: 'abbreviation',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 50 } }]
                    },
                    {
                        label: 'Website',
                        componentId: EditorType.link,
                        fieldName: 'webSite',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'Linkedin',
                        componentId: EditorType.link,
                        fieldName: 'linkedin',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'Type',
                        componentId: EditorType.companyType,
                        fieldName: 'companyTypeId',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Markets',
                        componentId: EditorType.entity,
                        fieldName: 'companyMarkets',
                        bindContext: this.company,
                        entityId: 'companyMarket',
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Salesperson',
                        componentId: EditorType.entityUser,
                        fieldName: 'companySalespersons',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Description',
                        componentId: EditorType.textarea,
                        fieldName: 'description',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 1000 } }]
                    }
                ],
                [EntityCardSection.CompanyMainContact]: [
                    {
                        componentId: EditorType.companyMainContact,
                        fieldName: 'contactId',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        additionalInfo: {
                            allowToNavigate: true
                        }
                    }
                ],
                [EntityCardSection.CompanyMainOffice]: [
                    {
                        componentId: EditorType.building,
                        fieldName: 'mainOfficeId',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        additionalInfo: {
                            allowToNavigate: true
                        }
                    }
                ],
                [EntityCardSection.CompanyAccountInfo]: [
                    {
                        componentId: EditorType.account,
                        fieldName: 'accountId',
                        bindContext: this.company,
                        isReadOnly: this.isReadOnly,
                        additionalInfo: {
                            allowToNavigate: true
                        }
                    }
                ],
                [EntityCardSection.CompanyCustomInfo]: []
            };

            if (!this.company.customFieldValues?.length)
                this.company.customFieldValues = await this.entityCustomFieldService.getCustomFieldsByEntityType(
                    EntityTypes.Company
                );

            this.company.customFieldValues.forEach((customField) => {
                customFieldPlacement(
                    this.cardsConfig,
                    customField,
                    EntityCardSection.CompanyCustomInfo,
                    !!this.isReadOnly
                );
            });
            await this.checkArchivedItems(this.company);
        }
    }

    validate(): boolean {
        const mainInfoCardValid = this.mainInfoCard ? this.mainInfoCard.validate() : false;
        const customInfoCard = this.customInfoCard ? this.customInfoCard.validate() : true; // if no custom fields: set as valid

        const allValid = mainInfoCardValid && customInfoCard;

        return allValid;
    }

    private async checkArchivedItems(company: CompanyView): Promise<void> {
        this.accountHasArchived = company.accountIsActive === false;
        this.buildingHasArchived = company.buildingIsActive === false;
        this.contactHasArchived = company.contactIsActive === false;
        this.usersHasArchived = company.companySalespersons?.some((c) => c.isActive === false) ?? false;
    }
}
